import { useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { denormalize, Schema } from 'normalizr';
import { PapiDeviation, PapiGallection, PapiUser } from '@wix/da-papi-types';
import { useOnScrollNearBottom } from '@wix/da-hooks/pkg/useOnScrollNearBottom';
import {
  selectors as streamSelectors,
  actions as streamActions,
} from '../../Stream';
import {
  createGetDenormalizedDataEntitiesByIds,
  getDataEntities,
  getDenormalizedDataEntitiesByIds,
} from '../../redux/entities/selectors';
import { GallectionType } from '../../types/gallection';
import {
  galleryFolderSchema,
  collectionFolderSchema,
} from '../../redux/normalizr/schemas/folder';
import { createGetUsersByIds } from '../../redux/users/selectors';

import { StreamInitConfig } from '../../Stream/base/types';
import { initializeIfNeeded } from '../../Stream/base/actions';
import { createSelector } from 'reselect';
import { deviationSchema } from '../../redux/normalizr/schemas/deviation';

export interface UseStreamReturnValue<T> {
  streamId: string;
  items: T[];
  total: number;
  hasMore: boolean;
  hasLess: boolean;
  isFetching: boolean;
  isInitialized: boolean;
  errorMessage?: string;
  itemsPerFetch?: number;

  fetchNext: () => void;
  fetchPrevious: () => void;
  fetchAll: () => void;
  fetchRetry: () => void;
  cancelFetch: () => void;
}

export function useStream<T = any>(
  streamId: string,
  throttleTimeout = 500,
  initConfig?: StreamInitConfig
): UseStreamReturnValue<T> {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!initConfig) {
      return;
    }

    dispatch(initializeIfNeeded(streamId, initConfig));
  }, [streamId, initConfig, dispatch]);

  const stream = useSelector(
    state => ({
      items: streamSelectors.getItems<T>(state, streamId),
      total: streamSelectors.getTotal(state, streamId),
      hasMore: streamSelectors.hasMore(state, streamId),
      hasLess: streamSelectors.getHasLess(state, streamId),
      isFetching: streamSelectors.isFetching(state, streamId),
      isInitialized: streamSelectors.isInitialized(state, streamId),
      errorMessage: streamSelectors.getErrorMsg(state, streamId),
      itemsPerFetch: streamSelectors.getItemsPerFetch(state, streamId),
    }),
    shallowEqual
  );

  const fetchNext = useMemo(() => {
    return throttle(() => {
      dispatch(streamActions.fetchNext(streamId));
    }, throttleTimeout);
  }, [dispatch, streamId, throttleTimeout]);

  const fetchPrevious = useMemo(() => {
    return throttle(() => {
      dispatch(streamActions.fetchPrevious(streamId));
    }, throttleTimeout);
  }, [dispatch, streamId, throttleTimeout]);

  const fetchAll = useMemo(() => {
    return throttle(() => {
      dispatch(streamActions.fetchAll(streamId));
    }, throttleTimeout);
  }, [dispatch, streamId, throttleTimeout]);

  const fetchRetry = useMemo(() => {
    return throttle(() => {
      dispatch(streamActions.fetchRetry(streamId));
    }, throttleTimeout);
  }, [dispatch, streamId, throttleTimeout]);

  const cancelFetch = useCallback(
    () => dispatch(streamActions.cancelFetchForStream(streamId)),
    [dispatch, streamId]
  );

  useEffect(() => {
    return () => {
      fetchNext.cancel();
      fetchPrevious.cancel();
      fetchAll.cancel();
      fetchRetry.cancel();
    };
  }, [fetchNext, fetchPrevious, fetchAll, fetchRetry]);

  return {
    ...stream,
    streamId,
    fetchNext,
    fetchPrevious,
    fetchAll,
    fetchRetry,
    cancelFetch,
  };
}

export function useNormalizedStream<T>(
  streamId: string,
  schema: Schema<T>,
  throttleTimeout = 500,
  initConfig?: StreamInitConfig
): UseStreamReturnValue<T> {
  const { items, ...restStream } = useStream(
    streamId,
    throttleTimeout,
    initConfig
  );

  const entities = useSelector(getDataEntities);
  const denormalizedItems = denormalize(items, schema, entities);

  return {
    ...restStream,
    items: denormalizedItems as T[],
  };
}

const getDenormalizedDeviations = createSelector(
  state => state,
  (_, deviationIds) => deviationIds,
  (state, deviationIds) =>
    getDenormalizedDataEntitiesByIds(state, deviationSchema, deviationIds)
);

export function useNormalizedDeviationStream(
  streamId: string,
  throttleTimeout = 500,
  initConfig?: StreamInitConfig
): UseStreamReturnValue<PapiDeviation> {
  const { items, ...restStream } = useStream(
    streamId,
    throttleTimeout,
    initConfig
  );

  const deviations = useSelector(state =>
    getDenormalizedDeviations(state, items)
  );

  return {
    ...restStream,
    items: deviations,
  };
}

export function useNormalizedUserStream(
  streamId: string,
  throttleTimeout = 500,
  initConfig?: StreamInitConfig
): UseStreamReturnValue<PapiUser> {
  const { items, ...restStream } = useStream<number>(
    streamId,
    throttleTimeout,
    initConfig
  );
  const getUsersByIds = useMemo(() => createGetUsersByIds(), []);
  const users = useSelector(state => getUsersByIds(state, items));

  return {
    ...restStream,
    items: users,
  };
}

export function useNormalizedFolderStream(
  streamId: string,
  gallectionType: GallectionType
): UseStreamReturnValue<PapiGallection> {
  const { items, ...restStream } = useStream<PapiGallection>(streamId);
  const getDenormalizedDataEntitiesByIds = useMemo(
    () => createGetDenormalizedDataEntitiesByIds<PapiGallection>(),
    []
  );
  const folders = useSelector(state =>
    getDenormalizedDataEntitiesByIds(
      state,
      gallectionType === 'gallery'
        ? galleryFolderSchema
        : collectionFolderSchema,
      items
    )
  );

  return {
    ...restStream,
    items: folders,
  };
}

/**
 * Creates an `onScroll` callback. Pass the returned callback to your scrollable area's `onScroll` prop. Then, whenever the user scrolls near the bottom
 * of the scrollable area, this will trigger a fetch.
 *
 * @param streamInfo The output of a `useStream` hook.
 * @param fetchDistance How far from the bottom should we trigger the fetch?
 */
export function useFetchOnScrollNearBottom(
  streamInfo: UseStreamReturnValue<any>,
  fetchDistance: number
) {
  const { streamId, isFetching, hasMore, fetchNext } = streamInfo;

  return useOnScrollNearBottom(
    fetchDistance,
    useCallback(() => {
      if (!streamId || isFetching || !hasMore) {
        return;
      }
      fetchNext();
    }, [streamId, isFetching, hasMore, fetchNext])
  );
}
